/**
 * Meta tags / SEO.
 *
 * These are the tags crawlers and chat apps read when a link to the game is
 * shared, so the screen shows the RESOLVED values next to the stored ones: the
 * fallbacks (title → brand, site name → title, locale → en_US) mean an empty
 * field is not the same as an empty tag, and an operator editing blind cannot
 * tell which is which.
 *
 * Image and favicon UPLOADS stay on the core endpoint, which validates the image
 * bytes and records their real dimensions. Here the URLs are typed, which is
 * what an operator needs when the files are already hosted — and the derived
 * width/height are shown read-only rather than as inputs that would be ignored.
 */
import { el, toast } from '../../dom';
import { adminApi, type AdminSeo } from '../../game-api';
import { btn, input, labelled, loadInto, panel, textarea, toastError } from './ui';

/** Label and help text per meta key, in the order an operator fills them in. */
const FIELDS: { key: string; label: string; hint?: string; long?: boolean }[] = [
  { key: 'title', label: 'Title', hint: 'The headline on a shared link. Falls back to the brand name.' },
  { key: 'description', label: 'Description', hint: 'One or two sentences under the title.', long: true },
  { key: 'site_name', label: 'Site name', hint: 'Falls back to the title.' },
  { key: 'url', label: 'Canonical URL', hint: 'Falls back to APP_URL.' },
  { key: 'locale', label: 'Locale', hint: 'e.g. en_US.' },
  { key: 'image', label: 'Share image URL', hint: 'The picture in the link preview. Setting it here clears the stored size.' },
  { key: 'image_alt', label: 'Share image alt text' },
  { key: 'twitter_card', label: 'Twitter card', hint: 'summary or summary_large_image.' },
  { key: 'icon_ico', label: 'Favicon (.ico)' },
  { key: 'icon_png_16', label: 'Icon 16×16' },
  { key: 'icon_png_32', label: 'Icon 32×32' },
  { key: 'icon_png_100', label: 'Icon 100×100' },
  { key: 'icon_png_180', label: 'Apple touch icon 180×180' },
  { key: 'icon_png_240', label: 'Apple touch icon 240×240' },
];

export function renderSeo(host: HTMLElement): () => void {
  return loadInto(host, () => adminApi.seo(), (doc, reload) => view(doc, reload), "Couldn't load the meta tags.");
}

function view(doc: AdminSeo, reload: () => void): HTMLElement {
  const editable = new Set(doc.editableKeys ?? []);
  const controls = new Map<string, HTMLInputElement | HTMLTextAreaElement>();
  const grid = el('div', 'grid gap-3', { style: 'grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));' });

  for (const f of FIELDS) {
    if (editable.size && !editable.has(f.key)) continue;
    const value = String(doc.values?.[f.key] ?? '');
    const control = f.long ? textarea(f.label, value, 3) : input(f.label, value);
    controls.set(f.key, control);
    grid.appendChild(el('div', 'min-w-0', {},
      labelled(f.label, control),
      f.hint ? el('div', 'text-[11px] text-text-dim mt-1', {}, f.hint) : null));
  }

  const derived = (doc.derivedKeys ?? []).map((k) =>
    el('div', 'text-[11px] text-text-dim', {}, `${k}: ${doc.values?.[k] || '— (set when an image is uploaded)'}`));

  const save = btn('Save meta tags', async () => {
    const settings: Record<string, string> = {};
    for (const [key, control] of controls) settings[key] = control.value.trim();
    try {
      await adminApi.saveSeo(settings);
      toast('Meta tags saved');
      reload();
    } catch (e) { toastError(e, 'Could not save the meta tags'); }
  }, 'primary');

  const resolved = doc.resolved ?? {};
  const preview = el('div', 'flex flex-col gap-1.5');
  for (const key of ['title', 'description', 'siteName', 'url', 'locale', 'image']) {
    const v = resolved[key];
    if (v == null || v === '') continue;
    preview.appendChild(el('div', 'text-xs', {},
      el('span', 'text-text-dim', {}, `${key}: `),
      el('span', '', {}, String(v))));
  }

  return el('div', 'flex flex-col gap-4', {},
    panel('Meta tags',
      el('div', 'text-xs text-text-dim mb-3', {},
        'What a crawler or a chat app shows when someone shares a link to the game. '
        + 'Leave a field empty to use its fallback.'),
      grid,
      derived.length
        ? el('div', 'mt-4 pt-3 border-t border-border/60 flex flex-col gap-1', {},
          el('div', 'text-[10px] uppercase tracking-widest text-text-dim mb-1', {}, 'Derived (read-only)'),
          ...derived)
        : null),
    panel('What the site actually serves', preview),
    el('div', 'flex justify-end', {}, save));
}
